import { AppStore } from "@/store";
import {
  AudioUploadDiagnosticEventType,
  AudioUploadManager,
  QueueDiagnosticEventType,
  type AudioUploadDiagnostics,
} from "@mem-client/uploads";
import { logger } from "@/modules/logger";
import {
  AudioUploadController,
  AudioUploadManagerCallbacks,
  AudioUploadProgressStateStore,
} from "@/store/audio/uploading/audio-uploading.types";
import { RendererAudioUploadProgressTracker } from "@/store/audio/uploading/renderer-audio-upload-progress-tracker";
import { WebAudioUploadPayloadProvider } from "@/store/audio/uploading/web-audio-upload-payload-provider";
import { WebAudioUploadTransport } from "@/store/audio/uploading/web-audio-upload-transport";
import { DesktopAudioUploadPayloadProvider } from "@/store/audio/uploading/desktop-audio-upload-payload-provider";
import { ElectronAudioUploadTransport } from "@/store/audio/uploading/electron-audio-upload-transport";
import { RecordingStrategyAudioUploadPayloadProvider } from "@/store/audio/uploading/recording-strategy-audio-upload-payload-provider";
import { RecordingStrategyAudioUploadTransport } from "@/store/audio/uploading/recording-strategy-audio-upload-transport";
import { RendererAudioUploadSource } from "@/store/audio/uploading/renderer-audio-upload-source";
import { RecordingStatusAudioUploadPersistence } from "@/store/audio/uploading/recording-status-audio-upload-persistence";

type CreateRendererAudioUploadControllerArgs = {
  store: AppStore;
  progressStore: AudioUploadProgressStateStore;
  callbacks?: AudioUploadManagerCallbacks;
};

export const createRendererAudioUploadController = ({
  store,
  progressStore,
  callbacks,
}: CreateRendererAudioUploadControllerArgs): AudioUploadController => {
  const webPayload = new WebAudioUploadPayloadProvider({
    recordings: store.dexie.repositories.audioRecordings,
    transcripts: store.dexie.repositories.localAudioRecordingTranscriptTurns,
    artifacts: store.dexie.repositories.webAudioArtifacts,
  });
  const desktopPayload = new DesktopAudioUploadPayloadProvider({
    recordings: store.dexie.repositories.audioRecordings,
    transcripts: store.dexie.repositories.localAudioRecordingTranscriptTurns,
  });
  const payload = new RecordingStrategyAudioUploadPayloadProvider({
    recordings: store.dexie.repositories.audioRecordings,
    webPayloadProvider: webPayload,
    desktopPayloadProvider: desktopPayload,
  });
  const webTransport = new WebAudioUploadTransport({
    artifacts: store.dexie.repositories.webAudioArtifacts,
  });
  const desktopTransport = new ElectronAudioUploadTransport();
  const transport = new RecordingStrategyAudioUploadTransport({
    webTransport,
    desktopTransport,
  });
  const persistence = new RecordingStatusAudioUploadPersistence({
    recordings: store.dexie.repositories.audioRecordings,
  });
  const progress = new RendererAudioUploadProgressTracker({
    progressStore,
  });
  const diagnostics: AudioUploadDiagnostics | undefined = store.interface.isDesktop
    ? {
        onEvent: ({ event }) => {
          switch (event.type) {
            case AudioUploadDiagnosticEventType.PrepareStarted:
            case AudioUploadDiagnosticEventType.PrepareSucceeded:
            case AudioUploadDiagnosticEventType.TransportStarted:
            case AudioUploadDiagnosticEventType.TransportSucceeded:
              logger.info({
                message: `[AudioUploadManager] ${event.type}`,
                info: event,
              });
              return;
            case AudioUploadDiagnosticEventType.PrepareFailed:
            case AudioUploadDiagnosticEventType.MarkUploadingFailed:
            case AudioUploadDiagnosticEventType.TransportFailed:
            case AudioUploadDiagnosticEventType.MarkCompletedFailed:
            case AudioUploadDiagnosticEventType.FinishWithError:
            case AudioUploadDiagnosticEventType.ErrorPersistenceFailed:
              logger.warn({
                message: `[AudioUploadManager] ${event.type}`,
                info: event,
              });
              return;
            default:
              return;
          }
        },
        queue: {
          onEvent: ({ event }) => {
            switch (event.type) {
              case QueueDiagnosticEventType.JobEnqueued:
              case QueueDiagnosticEventType.DuplicateJobMerged:
              case QueueDiagnosticEventType.DuplicateJobDropped:
              case QueueDiagnosticEventType.JobStarted:
              case QueueDiagnosticEventType.JobSucceeded:
                logger.info({
                  message: `[UploadQueueManager] ${event.type}`,
                  info: event,
                });
                return;
              case QueueDiagnosticEventType.JobFailed:
              case QueueDiagnosticEventType.QueueReset:
              case QueueDiagnosticEventType.StaleRunStopped:
                logger.warn({
                  message: `[UploadQueueManager] ${event.type}`,
                  info: event,
                });
                return;
              default:
                return;
            }
          },
        },
      }
    : undefined;

  return new AudioUploadManager<RendererAudioUploadSource>({
    handlers: {
      payload,
      transport,
      persistence,
      progress,
    },
    callbacks,
    diagnostics,
  });
};
